// useAuth.ts
import { useMutation } from '@tanstack/react-query';
import { authApi } from '../api/authApi';
import { create } from 'zustand';
import { 
  AuthResponse, 
  LoginCredentials, 
  AuthState,
  TwoFactorVerificationData
} from '../../types/auth';
import { sanitizeAndStore, getAndSanitize } from '../../shared/utils/sanitization';
import { useUserStore } from '../stores/useUserStore';
import { usePanelsStore } from '../stores/usePanelsStore';
import { useRequestsStore } from '../stores/useRequestsStore';
import { useRequestDetailsStore } from '../stores/useRequestDetailsStore';
import { useSettingsStore } from '../stores/useSettingsStore';

// Obtener referencia al QueryClient global
let globalQueryClient: any = null;

export const setGlobalQueryClient = (queryClient: any) => {
  globalQueryClient = queryClient;
};

// Función para limpiar completamente el estado de la aplicación
const clearAppState = () => {
  
  // Limpiar localStorage
  localStorage.removeItem('token');
  localStorage.removeItem('role');
  localStorage.removeItem('userId');
  localStorage.removeItem('user_permissions');
  localStorage.removeItem('adminId');
  
  // Limpiar cache de React Query de forma más agresiva
  if (globalQueryClient) {
    globalQueryClient.clear();
    globalQueryClient.getQueryCache().clear();
    globalQueryClient.getMutationCache().clear();
    
    // También invalidar todas las queries para forzar refetch
    globalQueryClient.invalidateQueries();
    globalQueryClient.refetchQueries();
  }
  
  // Limpiar todos los stores de la aplicación
  try {
    useUserStore.getState().clearUser();
    usePanelsStore.getState().clearPanels();
    useRequestsStore.getState().clearRequestsData();
    useRequestDetailsStore.getState().clearAllDetails();
    useSettingsStore.getState().clearSettings();
    
  } catch (error) {
    console.error('[Auth] Error clearing application stores:', error);
  }
};

// Función para precargar datos después del login exitoso
const reloadAppData = async (userId: string) => {
  
  try {
    // Esperar un poco para que se apliquen los cambios de estado
    await new Promise(resolve => setTimeout(resolve, 100));
    
    // Limpiar cache una vez más para estar seguros
    if (globalQueryClient) {
      globalQueryClient.clear();
      await globalQueryClient.invalidateQueries();
    }
    
    // Precargar datos del usuario con force refresh
    await useUserStore.getState().fetchUser(userId, true);
    
    // Forzar una recarga completa de la aplicación para garantizar estado limpio
    window.location.reload();
    
    return true;
  } catch (error) {
    console.error('[Auth] Error reloading application data:', error);
    // En caso de error, forzar recarga anyway
    window.location.reload();
    return false;
  }
};

export const useAuthStore = create<AuthState>((set) => ({
  isAuthenticated: !!getAndSanitize('token'),
  role: getAndSanitize('role'),
  token: getAndSanitize('token'),
  setAuth: (token: string, role: string) => {
    // Strip "Bearer" prefix if present (with or without space)
    const cleanToken = token.replace(/^Bearer\s*/i, '');
    sanitizeAndStore('token', cleanToken);
    sanitizeAndStore('role', role);
    set({ isAuthenticated: true, token: cleanToken, role });
  },
  logout: () => {
    console.trace('[Auth] LOGOUT STACK TRACE:');

    // Limpiar estado de autenticación
    set({
      isAuthenticated: false,
      token: null,
      role: null
    });
    
    // Limpiar todo el estado de la aplicación (incluyendo React Query)
    clearAppState();
    
    // Pequeño delay y luego forzar recarga para garantizar estado completamente limpio
    setTimeout(() => {
      window.location.href = '/';
    }, 100);
  },
}));

export const useAuth = () => {
  const { setAuth } = useAuthStore();

  const loginMutation = useMutation<AuthResponse, Error, LoginCredentials>({
    mutationFn: async (credentials) => {
      const response = await authApi.login(credentials);
      
      // Check if response contains a token (normal login flow)
      if (response?.data?.token) {
        const token = response.data.token.startsWith('Bearer ') 
          ? response.data.token 
          : `Bearer ${response.data.token}`;
        response.data.token = token;
      }
      
      // If 2FA is required, the response will not have a token but will have requires2FA=true
      if (response?.requires2FA) {
      }
      
      return response;
    },
    onSuccess: async (data) => {
      // Only set authentication if we have a token (not 2FA required case)
      if (data.data.token) {
        
        // Limpiar estado anterior primero
        clearAppState();
        
        // Establecer nueva autenticación
        setAuth(data.data.token, data.data.user.role);
        sanitizeAndStore('userId', data.data.user.id);
        
        if (data.data.permissions) {
          sanitizeAndStore('user_permissions', JSON.stringify(data.data.permissions));
        }
        
        // Recargar datos de la aplicación para el nuevo usuario
        await reloadAppData(data.data.user.id);
      }
    },
    onError: (error) => {
      console.error('[Auth] Login error:', error);
    }
  });

  const verify2FAMutation = useMutation<AuthResponse, Error, TwoFactorVerificationData>({
    mutationFn: async (verificationData) => {
      const response = await authApi.verify2FA(verificationData);
      
      if (response?.data?.token) {
        const token = response.data.token.startsWith('Bearer ') 
          ? response.data.token 
          : `Bearer ${response.data.token}`;
        response.data.token = token;
      }
      
      return response;
    },
    onSuccess: async (data) => {
      
      // Limpiar estado anterior primero
      clearAppState();
      
      // Establecer nueva autenticación
      setAuth(data.data.token, data.data.user.role);
      sanitizeAndStore('userId', data.data.user.id);
      if (data.data.permissions) {
        sanitizeAndStore('user_permissions', JSON.stringify(data.data.permissions));
      }
      
      // Recargar datos de la aplicación para el nuevo usuario
      await reloadAppData(data.data.user.id);
    },
    onError: (error) => {
      console.error('[Auth] 2FA verification error:', error);
    }
  });

  return { 
    loginMutation,
    verify2FAMutation
  };
};